import {
    ChartBarIcon,
    ChatBubbleLeftEllipsisIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";

/**
 * Customer case studies surfaced at /case-studies and /case-studies/:slug,
 * plus the "Case Studies" footer link. Each study cites a live customer
 * deployment with the FloGPT capabilities they shipped in production.
 *
 * Keep entries narrative-first: a short challenge, the concrete FloGPT
 * surfaces deployed, and quantified results. Internal product links use
 * the canonical /ai-* slugs so we lift SEO juice for each marketing page.
 */

export interface CaseStudyMetric {
    /** Headline value, e.g. "80%", "2.5M+", "<60s". */
    value: string;
    /** Short caption shown under the value. */
    label: string;
    /** Optional descriptor for screen readers / longer context. */
    description?: string;
}

export interface CaseStudySection {
    title: string;
    body: string;
}

export interface CaseStudyFeature {
    name: string;
    /** Internal FloGPT marketing route the customer leans on. */
    href: string;
    /** One-line description of how the customer uses this surface. */
    description: string;
}

export interface CaseStudy {
    slug: string;
    brand: string;
    domain: string;
    /** External link to the live customer site. */
    url: string;
    industry: string;
    /** Stage/segment tag, e.g. "Lead generation", "Customer support". */
    useCase: string;
    /** Tailwind gradient stops, e.g. "from-orange-500 to-pink-500". */
    accent: string;
    /** Tailwind soft-glow class, e.g. "bg-orange-500/15". */
    softAccent: string;
    Icon: ComponentType<{ className?: string }>;
    /** Compact card tagline (1 sentence). */
    tagline: string;
    /** Longer hero summary (2–3 sentences). */
    summary: string;
    challenge: string;
    solution: string;
    deployed: CaseStudyFeature[];
    results: CaseStudyMetric[];
    /** 3-5 "quick win" bullets for the highlights strip. */
    highlights: string[];
    /** Pull-quote shown above the CTA. */
    pullQuote?: { quote: string; attribution: string };
    seo: {
        title: string;
        description: string;
        keywords: string[];
    };
}

const SHARED_SUFFIX = "Case Study — FloGPT";

export const CASE_STUDIES: CaseStudy[] = [
    {
        slug: "outreachbox",
        brand: "OutreachBox",
        domain: "outreachbox.com",
        url: "https://outreachbox.com",
        industry: "B2B SaaS · Email outreach & lead generation",
        useCase: "Lead generation",
        accent: "from-orange-500 to-pink-500",
        softAccent: "bg-orange-500/15",
        Icon: EnvelopeIcon,
        tagline:
            "AI sales concierge that qualifies inbound demos and answers product questions for an email outreach platform.",
        summary:
            "OutreachBox runs a high-velocity outbound stack, so when a prospect lands on the marketing site they're already warm — and used to instant replies. FloGPT became the always-on AI sales rep that triages the inbound flood, books demos straight onto the founder's calendar, and routes deal-shaped questions into a real CRM ticket the team can pick up the next morning.",
        challenge:
            "OutreachBox's playbook generates spiky inbound volume — bursts of prospects who clicked through a cold email and want answers right now. A traditional chat widget that just collects an email forfeits the warmest moment of the funnel. The team needed a 24/7 sales-savvy responder that could speak the language of deliverability, warmup, and inbox rotation without sounding like a script.",
        solution:
            "The team embedded the FloGPT chat widget on outreachbox.com and grounded it in their docs, pricing, and product FAQ via the Knowledge Base. A Workflow Builder flow handles pricing objections, captures budget and team size, and books a demo via the calendar integration when the lead is qualified. Anything ambiguous lands as a CRM ticket assigned to the founder, with the full chat transcript and contact context attached.",
        deployed: [
            {
                name: "AI Chat Widget",
                href: "/ai-chat-widget",
                description: "Embedded site-wide, branded to match outreachbox.com's design system.",
            },
            {
                name: "Knowledge Base",
                href: "/ai-knowledge-base",
                description: "Crawls product docs, pricing, FAQ, and the changelog so answers stay current.",
            },
            {
                name: "Workflow Builder",
                href: "/ai-workflow-builder",
                description: "Qualifies leads, captures budget + team size, and routes to demo booking.",
            },
            {
                name: "Unified CRM Inbox",
                href: "/ai-crm",
                description: "Every unresolved chat lands as a ticket with full transcript and contact identity.",
            },
        ],
        results: [
            { value: "24/7", label: "AI sales coverage", description: "Inbound prospects never hit an empty inbox." },
            { value: "<10s", label: "First reply time", description: "Replaces a 4-hour async first-touch window." },
            { value: "1 inbox", label: "Chat + email + ticket unified", description: "Every conversation lands in the same CRM." },
        ],
        highlights: [
            "Grounded answers on deliverability, warmup, and inbox rotation — no canned fallback.",
            "Demo booking handed straight to the calendar — no back-and-forth scheduling.",
            "Ambiguous prospects become tickets, not lost browser tabs.",
            "Founder sees full conversation context before the first human reply.",
        ],
        pullQuote: {
            quote:
                "Prospects expect the same instant reply we give them in cold email. FloGPT closes that loop on our own site.",
            attribution: "OutreachBox · Growth team",
        },
        seo: {
            title: `OutreachBox ${SHARED_SUFFIX}`,
            description:
                "How OutreachBox uses FloGPT's AI chat widget, knowledge base, and unified CRM to qualify inbound demos and answer email-outreach product questions 24/7.",
            keywords: [
                "OutreachBox case study",
                "AI chat for SaaS",
                "lead qualification chatbot",
                "FloGPT case study",
                "email outreach AI",
            ],
        },
    },
    {
        slug: "pulsereal",
        brand: "Pulse Real",
        domain: "pulsereal.com",
        url: "https://pulsereal.com",
        industry: "Real estate · Investment analytics SaaS",
        useCase: "Customer support + lead generation",
        accent: "from-blue-500 to-indigo-500",
        softAccent: "bg-blue-500/15",
        Icon: ChartBarIcon,
        tagline:
            "AI analyst that walks investors through 2.5M+ properties across 15,000+ cities — and converts them into trial signups.",
        summary:
            "Pulse Real publishes deep short-term and long-term rental analytics across all 50 US states. Each visitor arrives with a different city, ZIP, or strategy in mind, so a static landing page can't keep up. FloGPT turned the site into a conversational analyst that answers market questions on the fly, surfaces the right dashboard, and onboards qualified investors into the 3-day trial without a human in the loop.",
        challenge:
            "Pulse Real's audience ranges from first-time STR investors to multi-property operators, and the questions cycle constantly — \"what's the cap rate in Tampa?\", \"how is Hawaii doing this quarter?\", \"is Nebraska a good long-term play?\". Routing every one of those through a human SDR doesn't scale. The team needed a self-serve answer layer that could speak in concrete numbers and gently nudge serious investors toward the trial.",
        solution:
            "FloGPT's chat widget sits across pulsereal.com, with the Knowledge Base grounded in the live market intelligence pages and FAQ. A Workflow Builder flow handles intent gating — investor segment, target market type (STR / long-term / homeowner), and ZIP focus — before serving up the matching dashboard link and a trial CTA. Outbound Triggers nudge trial signups that go cold on day 2 with a personalised re-engagement message.",
        deployed: [
            {
                name: "AI Chat Widget",
                href: "/ai-chat-widget",
                description: "Embedded site-wide as the always-on market analyst.",
            },
            {
                name: "Knowledge Base",
                href: "/ai-knowledge-base",
                description: "Grounded in state, city, and ZIP-level market intelligence + disclaimers.",
            },
            {
                name: "Workflow Builder",
                href: "/ai-workflow-builder",
                description: "Intent-gates by investor segment, strategy, and target market.",
            },
            {
                name: "Outbound Triggers",
                href: "/ai-outbound-triggers",
                description: "Re-engages cold trial signups on day 2 with a personalised follow-up.",
            },
        ],
        results: [
            { value: "2.5M+", label: "Properties answered on", description: "Live STR + long-term data in every reply." },
            { value: "15,420", label: "Cities queryable in chat", description: "From major metros to rural ZIPs." },
            { value: "3-day trial", label: "Funnel routed in-chat", description: "Qualified investors land in the trial flow." },
        ],
        highlights: [
            "Answers ground specific cap-rate, ADR, and occupancy questions instead of redirecting to docs.",
            "Walks investors through the right dashboard view for their strategy.",
            "Auto-attaches disclaimers on investment-advice questions to stay compliant.",
            "Trial-signup recovery triggered without writing a single cron.",
        ],
        pullQuote: {
            quote:
                "Our visitors want a number, not a brochure. FloGPT answers like an analyst and hands the warm ones straight to the trial.",
            attribution: "Pulse Real · Growth",
        },
        seo: {
            title: `Pulse Real ${SHARED_SUFFIX}`,
            description:
                "How Pulse Real uses FloGPT's AI chat widget, knowledge base, and outbound triggers to turn 2.5M+ real-estate data points into a conversational analyst and trial-signup funnel.",
            keywords: [
                "Pulse Real case study",
                "real estate AI chat",
                "STR analytics chatbot",
                "FloGPT case study",
                "AI for real estate SaaS",
            ],
        },
    },
    {
        slug: "inclusivespeak",
        brand: "InclusiveSpeak",
        domain: "inclusivespeak.com",
        url: "https://inclusivespeak.com",
        industry: "Accessibility · AAC for speech-difficulty support",
        useCase: "Customer support",
        accent: "from-violet-500 to-fuchsia-500",
        softAccent: "bg-violet-500/15",
        Icon: ChatBubbleLeftEllipsisIcon,
        tagline:
            "Compassionate support agent for parents and SLPs adopting an AAC platform — answers product, billing, and assessment questions with care.",
        summary:
            "InclusiveSpeak is an Augmentative and Alternative Communication (AAC) platform for children with speech difficulties. Their visitors aren't shopping — they're often a parent in the middle of a hard week or a speech-language pathologist evaluating tools mid-session. FloGPT became the always-available support layer that answers product, billing, and progress-tracking questions in plain, kind language, and triages anything sensitive to the human team.",
        challenge:
            "Support volume on an AAC product is high-empathy and high-stakes — questions about onboarding a non-verbal child, billing for a family, or interpreting progress analytics need careful, accurate answers. Inclusivespeak's small team couldn't be on chat 24/7, and a generic chatbot would feel cold for the audience. They needed an AI assistant that could be patient, factual, and know exactly when to escalate.",
        solution:
            "The team embedded FloGPT's chat widget on inclusivespeak.com, grounded in their feature docs, parent guides, and billing FAQs. A Workflow Builder flow steers users into one of four lanes — onboarding, customisation, progress tracking, or billing — and routes anything that mentions a child's wellbeing or a medical concern straight to a human ticket, with priority + SLA tracking via the CRM. The agent persona is tuned warm and slow — no aggressive upsells.",
        deployed: [
            {
                name: "AI Chat Widget",
                href: "/ai-chat-widget",
                description: "Calm, accessible chat surface tuned for parents and clinicians.",
            },
            {
                name: "Knowledge Base",
                href: "/ai-knowledge-base",
                description: "Grounded in feature docs, parent guides, AAC vocabulary, and billing FAQ.",
            },
            {
                name: "Workflow Builder",
                href: "/ai-workflow-builder",
                description: "Routes onboarding, customisation, progress, and billing into separate lanes.",
            },
            {
                name: "Unified CRM Inbox",
                href: "/ai-crm",
                description: "Sensitive conversations escalate to humans with priority + SLA tracking.",
            },
        ],
        results: [
            { value: "24/7", label: "Empathy-first support", description: "Available across timezones, every weekend." },
            { value: "4 lanes", label: "Specialised triage", description: "Onboarding, customisation, progress, billing." },
            { value: "Auto-escalate", label: "On sensitive intents", description: "Wellbeing questions go straight to humans." },
        ],
        highlights: [
            "Slow, warm conversational tone tuned for parents and clinicians.",
            "Knowledge base answers onboarding, customisation, and progress-tracking questions verbatim.",
            "Sensitive intents (wellbeing, medical) auto-route to a human with priority flag.",
            "Billing and account questions resolved without leaving the chat.",
        ],
        pullQuote: {
            quote:
                "Our families need answers, not hold music. FloGPT meets them where they are — and gets out of the way when a human is needed.",
            attribution: "InclusiveSpeak · Support",
        },
        seo: {
            title: `InclusiveSpeak ${SHARED_SUFFIX}`,
            description:
                "How InclusiveSpeak uses FloGPT's AI chat widget, knowledge base, and CRM escalation to give parents and SLPs warm, accessible support for their AAC platform — 24/7.",
            keywords: [
                "InclusiveSpeak case study",
                "AAC platform support",
                "accessibility chatbot",
                "FloGPT case study",
                "AI for accessibility SaaS",
            ],
        },
    },
];

export const CASE_STUDIES_BY_SLUG: Record<string, CaseStudy> = CASE_STUDIES.reduce(
    (acc, study) => {
        acc[study.slug] = study;
        return acc;
    },
    {} as Record<string, CaseStudy>,
);
